import type { FirebaseStorage } from 'firebase/storage'
import { getDownloadURL, ref } from 'firebase/storage'
import { isFirebaseStorageUrl } from '@breakoutlearning/firebase-repository/util'

// Port 9199 is the Storage emulator's port in firebase.json. The seed scripts
// write 127.0.0.1 URLs while the browser may reach the emulator under another
// host, so both are allowed. Empty outside emulator mode, so a deployed app
// never sends the token to a local address.
export const emulatorStorageOrigins: readonly string[] = __USE_EMULATOR__
  ? [
      `http://${import.meta.env.VITE_USE_EMULATOR_HOST || 'localhost'}:9199`,
      'http://127.0.0.1:9199',
    ]
  : []

/**
 * Whether a media URL may receive the viewer's Firebase ID token. Slide authors
 * choose media URLs, so the token goes only to Google Storage (or the local
 * emulator); any other server could read it from the request header (ENG-328).
 */
export function canSendFirebaseToken(
  url: string,
  allowedEmulatorOrigins: readonly string[]
): boolean {
  if (isFirebaseStorageUrl(url)) return true
  const origin = urlOrigin(url)
  if (!origin) return false
  return allowedEmulatorOrigins.includes(origin)
}

function urlOrigin(url: string): string | undefined {
  try {
    return new URL(url).origin
  } catch {
    // An unparseable URL is not app Storage, so it gets no token.
    return undefined
  }
}

/**
 * Check whether a URL is a GCS V4 signed URL.
 * Signed URLs contain an X-Goog-Signature query parameter.
 */
export function isSignedUrl(url: string | undefined | null): boolean {
  if (!url) return false
  return url.includes('X-Goog-Signature=')
}

/** VTT files are stored beside the original video, never beside .streaming.mp4. */
export function videoVttSidecarUrl(url: string | undefined | null) {
  if (!url || isSignedUrl(url)) return undefined
  const vttUrl = url.replace(/\.(mp4|webm)(?=(\?|$))/i, '.vtt')
  return vttUrl === url ? undefined : vttUrl
}

/**
 * Resolve a storage URL to a downloadable URL.
 * If the URL is already signed (from the API), return it directly.
 * Otherwise, use getDownloadURL to get a token-authenticated URL.
 */
export async function resolveStorageUrl(
  storage: FirebaseStorage,
  url: string
): Promise<string> {
  if (isSignedUrl(url)) return url
  const fileRef = ref(storage, url)
  return getDownloadURL(fileRef)
}

export async function decodeStorageUrl(storage: FirebaseStorage, url: string) {
  if (isSignedUrl(url)) return url

  const path = url.split('appspot.com/o/').pop()?.split('?').shift()
  const unescapedPath = decodeURIComponent(path || '')

  const fileRef = ref(storage, unescapedPath)
  const fileUrl = await getDownloadURL(fileRef)
  return fileUrl
}
